import React, { useEffect, useState, useCallback } from "react";
import { Routes, Route, useLocation } from "react-router-dom";
import { toast } from "sonner";
import RequireLawyer from "@lawyer/components/RequireLawyer";
import LawyerLayout from "@lawyer/layouts/LawyerLayout";
import LawyerLogin from "@lawyer/pages/LawyerLogin";
import LawyerDashboard from "@lawyer/pages/LawyerDashboard";
import LawyerConsultations from "@lawyer/pages/LawyerConsultations";
import LawyerEarnings from "@lawyer/pages/LawyerEarnings";
import LawyerProfile from "@lawyer/pages/LawyerProfile";
import LawyerReviews from "@lawyer/pages/LawyerReviews";
import LawyerKyc from "@lawyer/pages/LawyerKyc";
import { fetchDashboard, setAvailability } from "@lawyer/services/lawyerApi";

/**
 * Lawyer Portal — mounted at REACT_APP_LAWYER_PATH (default /lawyer-portal).
 *
 * The shell loads the dashboard payload once and shares it with all child
 * pages via props/context-on-the-fly to avoid duplicate /api/lawyer/dashboard
 * calls. Child pages can call `reload()` to refresh after mutations.
 */
function LawyerShell() {
  const [data, setData] = useState(null);
  const location = useLocation();

  const reload = useCallback(async () => {
    try { setData(await fetchDashboard()); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not load dashboard"); }
  }, []);

  useEffect(() => { reload(); }, [reload]);

  const onAvailabilityToggle = async () => {
    if (!data) return;
    const wasAvailable = !!data.lawyer.available;
    const next = !wasAvailable;
    // Optimistic flip BEFORE the network round-trip so the sidebar pill
    // updates within a single render frame.
    setData((prev) => prev ? {
      ...prev,
      lawyer: { ...prev.lawyer, available: next },
    } : prev);
    try {
      const updated = await setAvailability({ available: next });
      // Reconcile with authoritative server response (e.g. vacation_mode
      // may have forced available=false).
      setData((prev) => prev ? {
        ...prev,
        lawyer: { ...prev.lawyer,
          available: updated.available,
          vacation_mode: updated.vacation_mode,
          vacation_until: updated.vacation_until,
        },
      } : prev);
      toast.success(`You are now ${updated.available ? "online" : "offline"}`);
      reload();
    } catch (e) {
      // Roll back optimistic update.
      setData((prev) => prev ? {
        ...prev,
        lawyer: { ...prev.lawyer, available: wasAvailable },
      } : prev);
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  const onVacationToggle = async () => {
    if (!data) return;
    const wasVacation = !!data.lawyer.vacation_mode;
    const next = !wasVacation;
    setData((prev) => prev ? {
      ...prev,
      lawyer: { ...prev.lawyer,
        vacation_mode: next,
        // Vacation forces offline.
        available: next ? false : prev.lawyer.available,
      },
    } : prev);
    try {
      const updated = await setAvailability({ vacation_mode: next });
      setData((prev) => prev ? {
        ...prev,
        lawyer: { ...prev.lawyer,
          available: updated.available,
          vacation_mode: updated.vacation_mode,
          vacation_until: updated.vacation_until,
        },
      } : prev);
      toast.success(updated.vacation_mode ? "Vacation mode on — you're offline" : "Welcome back");
      reload();
    } catch (e) {
      setData((prev) => prev ? {
        ...prev,
        lawyer: { ...prev.lawyer, vacation_mode: wasVacation },
      } : prev);
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  return (
    <LawyerLayout
      available={data?.lawyer?.available || false}
      vacationMode={data?.lawyer?.vacation_mode || false}
      onAvailabilityToggle={onAvailabilityToggle}
      onVacationToggle={onVacationToggle}
    >
      <Routes>
        <Route index element={<LawyerDashboard data={data} onReload={reload} />} />
        <Route path="consultations" element={<LawyerConsultations />} />
        <Route path="earnings" element={<LawyerEarnings />} />
        <Route path="reviews" element={<LawyerReviews />} />
        <Route path="kyc" element={<LawyerKyc />} />
        <Route path="profile" element={<LawyerProfile />} />
      </Routes>
    </LawyerLayout>
  );
}

export default function LawyerApp() {
  return (
    <Routes>
      <Route path="login" element={<LawyerLogin />} />
      <Route path="*" element={
        <RequireLawyer>
          <LawyerShell />
        </RequireLawyer>
      } />
    </Routes>
  );
}
